import { useRef } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as z from "zod";

import { FETCH_ERROR } from "../consts/fetch-error";
import { useAlertMessage } from "../hooks/alert-message";
import { useMenu } from "../hooks/menu";
import { useProfileMenu } from "../hooks/profile-menu";
import { useSubMenu } from "../hooks/sub-menu";
import { logout } from "../services/auth";
import { on } from "../services/event-listner";
import { useAuthStore } from "../stores/auth";
import { useLocale } from "../stores/locale";
import { Alert } from "./alert";
import { Container } from "./container";
import { Layout } from "./layout";
import { SubMenu } from "./sub-menu";

export function Base({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate();
  const { setLocale } = useLocale();
  const authStore = useAuthStore();
  const alertMessage = useAlertMessage();

  const setIsAuthRef = useRef(authStore.setIsAuth);
  const navigateRef = useRef(navigate);

  const menu = useMenu();
  const subMenu = useSubMenu();
  const profileMenu = useProfileMenu();

  useEffect(() => {
    const subs = on("error", (payload: unknown) => {
      const parsedPayload = z
        .object({
          code: z.string(),
          message: z.string().optional(),
          info: z.string().optional(),
        })
        .parse(payload);

      if (parsedPayload.code === FETCH_ERROR.UNAUTHORIZED.toString()) {
        logout();
        setIsAuthRef.current(false);
        navigateRef.current("/");
      }

      alertMessage.open(parsedPayload);
      alertMessage.setIsOpen(true);
    });

    return () => {
      subs.unsubscribe();
    };
  });

  const color = "blue";

  return (
    <Layout
      availableLanguages={["en", "sv"]}
      color={color}
      menu={menu}
      name="Companies Admin"
      profileMenu={profileMenu || undefined}
      showSearch={false}
      onLanguageChange={(language: string) => {
        setLocale(language);
      }}
    >
      <Alert
        header={alertMessage.header}
        isOpen={alertMessage.isOpen}
        message={alertMessage.message}
        onClose={() => alertMessage.setIsOpen(false)}
      />
      <Container className="flex gap-5">
        {subMenu.length > 0 && (
          <div className="hidden md:block min-w-[230px]">
            <SubMenu color={color} menu={subMenu} />
          </div>
        )}
        <div className="flex-1 min-w-0">{children}</div>
      </Container>
    </Layout>
  );
}
